import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { Subscription } from "rxjs";

import { Version } from "src/app/shared/model/gestion-proyectos/etapa/version.model";
import {
  AMBIENTES_VERSION,
  AmbienteVersion,
  CODIGOS_VERSIONES,
  CrearVersionRespuesta,
  EditarVersionRespuesta,
  EtapasVersionesRespuesta,
  EtapaVersiones,
  IncidenciaVersion,
  LARGO_DETALLE_VERSION,
  LARGO_NOMBRE_VERSION,
  LARGO_RUTA_VERSION,
  ReferenciaVersion,
  VersionResumen,
} from "../proyecto-pagina/etapas-versiones.model";
import { EtapasVersionesService } from "../proyecto-pagina/etapas-versiones.service";
import {
  ChipEstado,
  chipEstadoVersion,
  fechasDeEtapa,
  lineaPendiente,
  lineaVersion,
  lineasDeAbiertas,
  motivoSinPermiso,
  nombreLibre,
  nombreSugerido,
  nuevoIdVersion,
  peticionCrearVersion,
  textoEntregaPendientes,
  versionesRecientesPrimero,
} from "../proyecto-pagina/etapas-versiones.util";
import { textoCreadaPor } from "../proyecto-pagina/pruebas-version.util";

/** Lo que recibe el diálogo: el proyecto y la etapa; el resto lo lee del servidor. */
export interface DatosNuevaVersion {
  /** El _id del proyecto en Mongo. */
  idProyecto: string;
  /** El _id de la etapa; null en una etapa guardada sin identificador. */
  idEtapa: string | null;
  nombreProyecto?: string | null;
  nombreEtapa?: string | null;
  /** Para decir por qué no se puede crear (D8, proyecto cerrado). */
  estatusProyecto?: string | null;
  /**
   * «Editar versión» (desde A2, §5.5): la versión que se edita. Solo cambian
   * el nombre (único en la etapa), la ruta y la descripción.
   */
  version?: VersionResumen | null;
}

/**
 * Lo que da afterClosed(): la versión creada, o version null si se cerró sin
 * confirmarla pero la lista quedó vieja (otra versión entró, o no se supo si
 * la petición llegó). undefined = se canceló sin cambios.
 */
export interface ResultadoNuevaVersion {
  version: Version | null;
  versionCerrada: Version | null;
  etapa: EtapaVersiones | null;
  /** Las resoluciones que de verdad entregó (T3b); vacío si no se llevó ninguna. */
  incidenciasEntregadas?: IncidenciaVersion[];
  /** La versión se editó (no se creó): version trae cómo quedó. */
  editada?: boolean;
}

/** Una versión en la columna izquierda, ya con su chip y su renglón. */
interface FilaVersion {
  id: string;
  nombre: string;
  chip: ChipEstado;
  linea: string;
  /** La que se está editando (T1 en edición). */
  enEdicion: boolean;
}

/** Una resolución que espera versión (T3b), ya con su renglón. */
interface FilaPendiente {
  id: number;
  titulo: string;
  linea: string;
}

interface ControlesNuevaVersion {
  nombre: FormControl<string>;
  ambiente: FormControl<AmbienteVersion>;
  origen: FormControl<string | null>;
  ruta: FormControl<string>;
  detalle: FormControl<string>;
}

const ID_TITULO = "nueva-version-titulo";
const ID_NOMBRE = "nueva-version-nombre";

/**
 * Abre «Nueva versión» (T1) con la forma de los diálogos estándar
 * (app-standard-dialog y la caja .app-dialog de styles.css), debajo de la
 * barra superior fija.
 */
export function abrirDialogoNuevaVersion(
  dialog: MatDialog,
  datos: DatosNuevaVersion,
): MatDialogRef<ModalAgregarVersionComponent, ResultadoNuevaVersion> {
  return dialog.open<ModalAgregarVersionComponent, DatosNuevaVersion, ResultadoNuevaVersion>(ModalAgregarVersionComponent, {
    data: datos,
    width: "960px",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 106px)",
    position: { top: "90px" },
    panelClass: "app-standard-dialog",
    backdropClass: "app-standard-dialog-backdrop",
    ariaLabelledBy: ID_TITULO,
    // El foco va al cuadro y, cuando llega la etapa, al nombre (aplicarEtapa).
    autoFocus: "dialog",
    disableClose: true,
  });
}

/**
 * Gestión de Proyectos · «Nueva versión» (T1; plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §5.4 y §8). El
 * servidor es el único dueño de las versiones: el diálogo ya no guarda el
 * proyecto completo, llama a PROYECTOS_VERSION_CREAR.
 *
 * - Al abrir lee la etapa con PROYECTOS_ETAPAS_VERSIONES: sus versiones con
 *   su estado, las abiertas por versión, el nombre sugerido (D14), los
 *   orígenes QA para una PRODUCTIVO (D3) y si el usuario puede crear (D4,
 *   D8). Sin permiso dice por qué y no deja guardar.
 * - Idempotencia: el id de la versión se genera al abrir y no cambia; un
 *   doble clic o un reintento crean una sola versión. Si el reintento lleva
 *   otros datos (se corrigió algo después de un error), el servidor no lo da
 *   por creado: VAL-VER-CONFLICTO con la etapa al día, que ya trae la versión
 *   del primer intento; desde ahí el diálogo usa un id nuevo.
 * - Conflicto: manda la vigente que vio (idVersionVigenteEsperada). Si otra
 *   versión entró mientras capturaba, el servidor responde
 *   VAL-VER-CONFLICTO con la etapa al día; el diálogo se refresca y lo dice.
 * - Los demás rechazos del servidor (nombre repetido, origen inválido,
 *   proyecto cerrado, sin permiso) se muestran como error. Con una falla
 *   inesperada (00001) no se sabe si la versión entró: al cerrar se recarga la
 *   lista, y reintentar con el mismo id no la duplica.
 *
 * Resoluciones pendientes de versión (T3b, Fase 3): con la bandera encendida
 * (entregaAutomatica), o si la etapa ya tiene pendientes, el diálogo lista
 * las que se entregan y el aviso dice que pasan a validación de QA; una
 * PRODUCTIVO no se las lleva (D3) y lo dice. Sin bandera ni pendientes, el
 * aviso solo dice qué versión pasa a Cerrada.
 */
@Component({
  selector: "app-modal-agregar-version",
  standalone: true,
  templateUrl: "./modal-agregar-version.component.html",
  styleUrls: ["./modal-agregar-version.component.css"],
  imports: [CommonModule, ReactiveFormsModule, MatDialogContent, MatDialogActions, MatFormFieldModule, MatInputModule, MatSelectModule],
})
export class ModalAgregarVersionComponent implements OnInit, OnDestroy {
  readonly idTitulo = ID_TITULO;
  readonly idNombre = ID_NOMBRE;
  readonly ambientes = AMBIENTES_VERSION;
  readonly largoNombre = LARGO_NOMBRE_VERSION;
  readonly largoRuta = LARGO_RUTA_VERSION;
  readonly largoDetalle = LARGO_DETALLE_VERSION;
  /**
   * La llave de idempotencia: se genera al abrir y no cambia mientras el
   * diálogo siga abierto, salvo cuando la etapa ya trae una versión con ese
   * id (un intento anterior sí la creó y el servidor rechazó el reintento con
   * otros datos): el siguiente «Guardar versión» es otra versión.
   */
  idVersion = nuevoIdVersion();
  readonly subtitulo: string;
  /** T1 en modo edición (§5.5): la versión que se edita viene en los datos. */
  readonly esEdicion: boolean;

  readonly formulario = new FormGroup<ControlesNuevaVersion>({
    nombre: new FormControl("", {
      nonNullable: true,
      validators: [Validators.maxLength(LARGO_NOMBRE_VERSION), (c) => this.validaNombre(c)],
    }),
    ambiente: new FormControl<AmbienteVersion>("QA", { nonNullable: true, validators: [Validators.required] }),
    origen: new FormControl<string | null>(null, { validators: [(c) => this.validaOrigen(c)] }),
    ruta: new FormControl("", { nonNullable: true, validators: [Validators.maxLength(LARGO_RUTA_VERSION)] }),
    detalle: new FormControl("", { nonNullable: true, validators: [Validators.maxLength(LARGO_DETALLE_VERSION)] }),
  });

  /* ---------- Lo que se lee del servidor ---------- */
  etapa: EtapaVersiones | null = null;
  filas: FilaVersion[] = [];
  /** Las resoluciones que esperan versión en la etapa (T3b). */
  pendientes: FilaPendiente[] = [];
  /** La sección de entregas: con la bandera, o si ya hay pendientes (se entregan con o sin bandera). */
  muestraEntregas = false;
  lineasAbiertas: string[] = [];
  fechas = "";
  motivo = "";

  /* ---------- Lo que se dice ---------- */
  cargando = false;
  /** No se pudo leer la etapa: no hay formulario. */
  errorCarga = "";
  guardando = false;
  /** El rechazo del servidor o la falta de conexión al guardar. */
  mensaje = "";
  /** VAL-VER-CONFLICTO: quién creó qué mientras se capturaba. */
  conflicto = "";

  private nombreSugeridoActual = "";
  /** Ya se leyó la etapa una vez: una relectura respeta lo que se escribió. */
  private leida = false;
  /** La lista de la tarjeta quedó vieja aunque no se cree nada (conflicto, o no se supo si llegó). */
  private recargarAlCerrar = false;
  private cerrado = false;
  private readonly subs: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly datos: DatosNuevaVersion,
    private readonly dialogRef: MatDialogRef<ModalAgregarVersionComponent, ResultadoNuevaVersion>,
    private readonly servicio: EtapasVersionesService,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    this.subtitulo = [datos?.nombreProyecto, datos?.nombreEtapa].map((p) => (p ?? "").trim()).filter(Boolean).join(" · ");
    this.esEdicion = !!datos?.version?.id;
  }

  ngOnInit(): void {
    this.subs.push(
      this.dialogRef.backdropClick().subscribe(() => this.cancelar()),
      this.dialogRef.keydownEvents().subscribe((ev) => {
        if (ev.key === "Escape") {
          ev.preventDefault();
          this.cancelar();
        }
      }),
    );
    this.cargar();
  }

  ngOnDestroy(): void {
    this.cerrado = true;
    this.subs.forEach((s) => s.unsubscribe());
  }

  /* ============================ Lo que pinta la plantilla ============================ */

  /** «Nueva versión» o «Editar versión V3-13-08-2026». */
  get titulo(): string {
    return this.esEdicion ? `Editar versión ${this.versionEditada?.nombre ?? this.datos.version?.nombre ?? ""}`.trim() : "Nueva versión";
  }

  /** La versión que se edita, como la trae la etapa leída (la de los datos si todavía no llega). */
  get versionEditada(): VersionResumen | null {
    const id = this.datos.version?.id;
    return this.etapa?.versiones?.find((v) => v.id === id) ?? this.datos.version ?? null;
  }

  /** En edición: «Creada el 13/08/2026 por Irving Guzmán Salas · consecutivo 3». */
  get lineaCreacion(): string | null {
    const version = this.esEdicion ? this.versionEditada : null;
    if (!version) {
      return null;
    }
    return version.consecutivo ? `${textoCreadaPor(version)} · consecutivo ${version.consecutivo}` : textoCreadaPor(version);
  }

  get textoGuardar(): string {
    if (this.guardando) {
      return "Guardando…";
    }
    return this.esEdicion ? "Guardar cambios" : "Guardar versión";
  }

  get esProductivo(): boolean {
    return this.formulario.controls.ambiente.value === "PRODUCTIVO";
  }

  get origenesQA(): ReferenciaVersion[] {
    return this.etapa?.origenesQA ?? [];
  }

  /** «Incidencias que se entregan en esta versión (3)»; con PRODUCTIVO no se entregan (D3). */
  get tituloEntregas(): string {
    const cuantas = this.pendientes.length;
    return this.esProductivo
      ? `Resoluciones pendientes de versión (${cuantas})`
      : `Incidencias que se entregan en esta versión (${cuantas})`;
  }

  /** Lo que se dice de las pendientes en el aviso (§1.3, D3); null sin pendientes. */
  get avisoEntregas(): string | null {
    return this.muestraEntregas
      ? textoEntregaPendientes(this.pendientes.length, this.formulario.controls.nombre.value, this.formulario.controls.ambiente.value)
      : null;
  }

  /** Se puede escribir: la etapa se leyó, hay permiso y no se está guardando. */
  get editable(): boolean {
    return !!this.etapa?.puedeCrearVersion && !this.guardando;
  }

  get puedeGuardar(): boolean {
    return this.editable && this.formulario.valid;
  }

  largo(campo: "nombre" | "detalle"): number {
    return this.formulario.controls[campo].value.length;
  }

  error(campo: keyof ControlesNuevaVersion, tipo: string): boolean {
    return this.formulario.controls[campo].hasError(tipo);
  }

  trackFila(_: number, fila: FilaVersion): string {
    return fila.id;
  }

  trackPendiente(_: number, fila: FilaPendiente): number {
    return fila.id;
  }

  /* ============================ Leer la etapa ============================ */

  cargar(): void {
    if (this.cargando || this.cerrado) {
      return;
    }
    if (!this.datos?.idProyecto || !this.datos.idEtapa) {
      this.errorCarga = "Esta etapa no tiene identificador: no se le pueden crear versiones hasta corregir sus datos.";
      this.pinta();
      return;
    }
    this.cargando = true;
    this.errorCarga = "";
    this.pinta();
    this.subs.push(
      this.servicio.etapasVersiones(this.datos.idProyecto).subscribe({
        next: (res) => this.alCargar(res),
        error: () => {
          this.cargando = false;
          this.errorCarga = "No se pudo leer la etapa: sin conexión con el servidor.";
          this.pinta();
        },
      }),
    );
  }

  private alCargar(res: EtapasVersionesRespuesta | null): void {
    this.cargando = false;
    if (res?.pCodRetorno !== CODIGOS_VERSIONES.EXITO) {
      this.errorCarga = res?.pMensaje?.trim() || "No se pudo leer la etapa.";
      this.pinta();
      return;
    }
    const etapa = (res.etapas ?? []).find((e) => !!e.idEtapa && e.idEtapa === this.datos.idEtapa);
    if (!etapa) {
      this.errorCarga = "La etapa ya no está en el proyecto. Cierra y vuelve a consultar la lista.";
      this.pinta();
      return;
    }
    if (this.esEdicion && !etapa.versiones?.some((v) => v.id === this.datos.version?.id)) {
      this.errorCarga = "La versión ya no está en la etapa. Cierra y vuelve a consultar el detalle.";
      this.pinta();
      return;
    }
    // Primera lectura: el nombre sugerido (o el de la versión que se edita)
    // llena el campo. Una relectura (después de un conflicto sin etapa en la
    // respuesta) respeta lo que se escribió.
    this.aplicarEtapa(etapa, !this.leida);
  }

  /**
   * Pinta la etapa (al abrir o la que trae un conflicto). El nombre sugerido
   * se renueva si el usuario no lo cambió, y un origen que ya no está
   * disponible se quita.
   */
  private aplicarEtapa(etapa: EtapaVersiones, inicial: boolean): void {
    const sugeridoAntes = this.nombreSugeridoActual;
    this.etapa = etapa;
    this.leida = true;
    if (!this.esEdicion && etapa.versiones?.some((v) => v.id === this.idVersion)) {
      this.idVersion = nuevoIdVersion();
    }
    const idEditada = this.datos.version?.id ?? null;
    this.filas = versionesRecientesPrimero(etapa.versiones).map((v, i) => ({
      id: v.id ?? `sin-id-${i}`,
      nombre: v.nombre?.trim() || "Versión sin nombre",
      chip: chipEstadoVersion(v.estado),
      linea: lineaVersion(v),
      enEdicion: !!idEditada && v.id === idEditada,
    }));
    this.pendientes = (etapa.pendientes ?? []).map((incidencia) => ({
      id: incidencia.idIncidencia,
      titulo: incidencia.titulo?.trim() || "Incidencia sin título",
      linea: lineaPendiente(incidencia),
    }));
    this.muestraEntregas = etapa.entregaAutomatica || this.pendientes.length > 0;
    this.lineasAbiertas = lineasDeAbiertas(etapa);
    this.fechas = fechasDeEtapa(etapa);
    this.motivo = etapa.puedeCrearVersion ? "" : motivoSinPermiso(this.datos.estatusProyecto, this.esEdicion);
    const { nombre, origen } = this.formulario.controls;
    if (this.esEdicion) {
      this.aplicarVersionEditada(inicial);
    } else {
      this.nombreSugeridoActual = etapa.nombreSugerido?.trim() || nombreSugerido(etapa.versiones, new Date());
      const capturado = nombre.value.trim();
      if (inicial || !capturado || capturado === sugeridoAntes) {
        nombre.setValue(this.nombreSugeridoActual);
      }
    }
    if (origen.value && !this.origenesQA.some((o) => o.id === origen.value)) {
      origen.setValue(null);
    }
    this.habilitar();
    nombre.updateValueAndValidity();
    origen.updateValueAndValidity();
    this.pinta();
    if (inicial && this.editable) {
      // El campo nace con la etapa: al abrir todavía no existe.
      setTimeout(() => this.host.nativeElement.querySelector<HTMLInputElement>(`#${ID_NOMBRE}`)?.focus());
    }
  }

  /**
   * T1 en edición: al abrir, los datos de la versión como están guardados;
   * el ambiente se muestra y no se edita (§5.5).
   */
  private aplicarVersionEditada(inicial: boolean): void {
    const version = this.versionEditada;
    if (!inicial || !version) {
      return;
    }
    this.formulario.patchValue({
      nombre: version.nombre ?? "",
      ambiente: version.ambiente === "PRODUCTIVO" ? "PRODUCTIVO" : "QA",
      origen: null,
      ruta: version.ruta ?? "",
      detalle: version.detalle ?? "",
    });
  }

  /* ============================ Capturar ============================ */

  cambioAmbiente(): void {
    const { origen } = this.formulario.controls;
    if (!this.esProductivo) {
      origen.setValue(null);
    }
    origen.markAsUntouched();
    origen.updateValueAndValidity();
    this.cambio();
  }

  /** Cada cambio borra el error anterior del servidor. */
  cambio(): void {
    if (!this.guardando && this.mensaje) {
      this.mensaje = "";
    }
  }

  limpiar(): void {
    if (!this.editable) {
      return;
    }
    this.formulario.reset({ nombre: this.nombreSugeridoActual, ambiente: "QA", origen: null, ruta: "", detalle: "" });
    this.mensaje = "";
    this.pinta();
  }

  /**
   * D14: no vacío y único en la etapa sin distinguir mayúsculas (los
   * repetidos viejos se dejan). En edición, el nombre que ya tenía la versión
   * no se revisa, como en el servidor.
   */
  private validaNombre(control: AbstractControl): ValidationErrors | null {
    const valor = String(control.value ?? "");
    if (!valor.trim()) {
      return { required: true };
    }
    const original = (this.datos?.version?.nombre ?? "").trim().toLowerCase();
    if (this.esEdicion && valor.trim().toLowerCase() === original) {
      return null;
    }
    return nombreLibre(this.etapa?.versiones, valor) ? null : { repetido: true };
  }

  /** D3: una PRODUCTIVO necesita una versión QA de la etapa que no sea origen de otra. */
  private validaOrigen(control: AbstractControl): ValidationErrors | null {
    // Con el padre: al construir el formulario el control todavía no lo tiene.
    if (control.parent?.get("ambiente")?.value !== "PRODUCTIVO") {
      return null;
    }
    if (!control.value) {
      return { required: true };
    }
    return this.origenesQA.some((o) => o.id === control.value) ? null : { noDisponible: true };
  }

  /* ============================ Guardar ============================ */

  guardar(): void {
    const etapa = this.etapa;
    if (!etapa?.idEtapa || !this.editable) {
      return;
    }
    this.formulario.markAllAsTouched();
    if (this.formulario.invalid) {
      this.pinta();
      return;
    }
    if (this.esEdicion) {
      this.guardarEdicion(etapa.idEtapa);
      return;
    }
    const peticion = peticionCrearVersion(this.idVersion, this.datos.idProyecto, etapa, this.formulario.getRawValue());
    this.guardando = true;
    this.mensaje = "";
    this.conflicto = "";
    this.habilitar();
    this.pinta();
    this.subs.push(
      this.servicio.crearVersion(peticion).subscribe({
        next: (res) => this.alGuardar(res),
        error: () => {
          // No se sabe si llegó: el mismo id hace que reintentar no duplique.
          this.recargarAlCerrar = true;
          this.falla(
            "No se pudo confirmar la versión: sin conexión con el servidor. Vuelve a pulsar «Guardar versión»; si ya se había creado, no se duplica.",
          );
        },
      }),
    );
  }

  private alGuardar(res: CrearVersionRespuesta | null): void {
    if (!res) {
      this.falla("No se pudo crear la versión: el servidor no respondió nada.");
      return;
    }
    if (res.pCodRetorno === CODIGOS_VERSIONES.EXITO) {
      this.guardando = false;
      this.cierra({
        version: res.version,
        versionCerrada: res.versionCerrada,
        etapa: res.etapa,
        incidenciasEntregadas: res.incidenciasEntregadas ?? [],
      });
      return;
    }
    if (res.pCodRetorno === CODIGOS_VERSIONES.CONFLICTO) {
      this.guardando = false;
      this.recargarAlCerrar = true;
      this.conflicto =
        res.pMensaje?.trim() || "Mientras capturabas cambiaron las versiones de la etapa. Revisa antes de crear otra.";
      if (res.etapa) {
        this.aplicarEtapa(res.etapa, false);
      } else {
        this.etapa = null;
        this.cargar();
      }
      return;
    }
    if (!(res.pCodRetorno ?? "").startsWith("VAL-")) {
      // Una falla inesperada (00001) no dice si el $push entró: la lista de la
      // tarjeta se recarga al cerrar y reintentar con el mismo id no duplica.
      this.recargarAlCerrar = true;
    }
    this.falla(res.pMensaje?.trim() || "No se pudo crear la versión.");
  }

  /**
   * T1 en edición: PROYECTOS_VERSION_EDITAR con nombre, ruta y descripción
   * (§5.5) y la versión como la mostró el diálogo (la última leída), para
   * que el servidor detecte si otro la cambió mientras estaba abierto.
   */
  private guardarEdicion(idEtapa: string): void {
    const { nombre, ruta, detalle } = this.formulario.getRawValue();
    const vista = this.versionEditada;
    this.guardando = true;
    this.mensaje = "";
    this.conflicto = "";
    this.habilitar();
    this.pinta();
    this.subs.push(
      this.servicio
        .editarVersion({
          idProyecto: this.datos.idProyecto,
          idEtapa,
          id: this.datos.version?.id ?? "",
          nombre: nombre.trim(),
          ruta: ruta.trim() || null,
          detalle: detalle.trim() || null,
          nombreEsperado: vista?.nombre ?? null,
          rutaEsperada: vista?.ruta ?? null,
          detalleEsperado: vista?.detalle ?? null,
        })
        .subscribe({
          next: (res) => this.alEditar(res),
          error: () => this.falla("No se pudo confirmar el cambio: sin conexión con el servidor. Vuelve a pulsar «Guardar cambios»."),
        }),
    );
  }

  private alEditar(res: EditarVersionRespuesta | null): void {
    if (res?.pCodRetorno === CODIGOS_VERSIONES.EXITO) {
      this.guardando = false;
      this.cierra({ version: res.version, versionCerrada: null, etapa: null, editada: true });
      return;
    }
    if (res?.pCodRetorno === CODIGOS_VERSIONES.CONFLICTO) {
      // Otro la cambió mientras el diálogo estaba abierto: se relee la etapa
      // (el nombre único se revisa contra lo nuevo y la versión leída es la
      // que se manda como esperada al volver a guardar) y se conserva lo escrito.
      this.guardando = false;
      this.recargarAlCerrar = true;
      this.conflicto = res.pMensaje?.trim() || "Alguien más cambió la versión mientras la editabas.";
      this.etapa = null;
      this.cargar();
      return;
    }
    this.falla(res?.pMensaje?.trim() || "No se pudo guardar el cambio de la versión.");
  }

  private falla(texto: string): void {
    this.guardando = false;
    this.mensaje = texto;
    this.habilitar();
    this.pinta();
  }

  /* ============================ Cerrar ============================ */

  cancelar(): void {
    if (this.guardando || this.cerrado) {
      return;
    }
    if (this.recargarAlCerrar) {
      this.cierra({ version: null, versionCerrada: null, etapa: null });
      return;
    }
    this.cerrado = true;
    this.dialogRef.close();
  }

  private cierra(resultado: ResultadoNuevaVersion): void {
    if (this.cerrado) {
      return;
    }
    this.cerrado = true;
    this.dialogRef.close(resultado);
  }

  /* ============================ Utilidades ============================ */

  /** Los campos se bloquean sin permiso y mientras se guarda. */
  private habilitar(): void {
    if (this.editable) {
      this.formulario.enable({ emitEvent: false });
    } else {
      this.formulario.disable({ emitEvent: false });
    }
    if (this.esEdicion) {
      // El ambiente y el origen no se editan (§5.5).
      this.formulario.controls.ambiente.disable({ emitEvent: false });
      this.formulario.controls.origen.disable({ emitEvent: false });
    }
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.cerrado) {
      this.cdr.detectChanges();
    }
  }
}
